import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, CreditCard, Loader2, Wallet } from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import Txt from "../../components/Text";
import Breadcrumbs from "../../components/private/Breadcrumbs";
import SubmitLoading from "../../components/common/SubmitLoading";
import api from "../../api";
import {
  apiFetchError,
  apiRequest,
  apiSuccess,
} from "../../redux/api/apiActions";
import { showAlert } from "../../redux/alert/alertActions";
import { arClass } from "../../utils/useArClass";
import textTranslator from "../../utils/useTextTranslator";
import formatDate from "../../utils/formatDate";

const loadVisitKey = "visit-payment-load-visit";
const createPaymentKey = "visit-payment-create";

const paymentMethods = [
  "cash",
  "credit-card",
  "debit-card",
  "insurance",
  "online-payment",
  "other",
];

const paymentStatuses = [
  "pending",
  "paid",
  "completed",
  "failed",
  "refunded",
  "cancelled",
  "on-hold",
];

const Text = ({ children, className, page = "PAYMENTS" }) => {
  return (
    <Txt page={page} className={className} keyName={page}>
      {children}
    </Txt>
  );
};

const t = (value, page = "PAYMENTS") => textTranslator(value, page) || value;

const displayValue = (value) => {
  if (value === null || value === undefined || value === "") return "-";
  return String(value);
};

const convertStageToStatus = (stage) => {
  switch (String(stage || "").toLowerCase()) {
    case "nurse":
    return "Patient triage with nurse";
  case "doctor":
    return "Visit with doctor";
  case "finished":
  case "done":
    return "Visit completed";
  case "cancelled":
    return "cancelled";
  case "absent":
    return "absent";
  case "payment":
    return "Payment in progress";
  default:
    return "waiting";
  }
};

const VisitPayment = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();
  const darkMode = useSelector((state) => state.settings.darkMode);
  const defaultLanguage = useSelector((state) => state.settings.defaultLanguage);
  const requests = useSelector((state) => state.api.requests || {});

  const isLoadingVisit = requests[loadVisitKey]?.loading;
  const isSubmitting = requests[createPaymentKey]?.loading;

  const [visit, setVisit] = useState(null);
  const [visitPayload, setVisitPayload] = useState(null);
  const [form, setForm] = useState({
    amount: "",
    method: "cash",
    status: "pending",
  });

  const colors = useMemo(
    () => ({
      background: darkMode ? "bg-gray-900" : "bg-gray-50",
      card: darkMode
        ? "bg-gray-800 border-gray-700"
        : "bg-white border-gray-200",
      input: darkMode
        ? "bg-gray-900 border-gray-700 text-slate-100"
        : "bg-white border-gray-200 text-slate-700",
      text: {
        primary: darkMode ? "text-white" : "text-gray-800",
        secondary: darkMode ? "text-gray-300" : "text-gray-600",
      },
      button: {
        primary: darkMode
          ? "bg-blue-600 text-white hover:bg-blue-500"
          : "bg-blue-500 text-white hover:bg-blue-600",
      },
    }),
    [darkMode],
  );

  const updateForm = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const loadVisit = async () => {
    if (!id) {
      dispatch(
        showAlert({
          type: "error",
          title: t("Missing visit ID"),
          message: t("Visit ID is required in the URL."),
        }),
      );
      return;
    }

    try {
      dispatch(apiRequest("GET", loadVisitKey));
      const res = await api.get(`visits/visit-id/${id}`);
      const payload = res?.data || null;
      const visitData = payload?.visit || payload;

      if (!visitData?.id) {
        throw new Error("Visit not found");
      }

      const existingPayment = payload?.payment || visitData?.visitPayment || null;

      setVisitPayload(payload);
      setVisit(visitData);
      if (existingPayment) {
        setForm((prev) => ({
          ...prev,
          amount:
            existingPayment.amount === null || existingPayment.amount === undefined
              ? prev.amount
              : String(existingPayment.amount),
          method: existingPayment.method || prev.method,
          status: existingPayment.status || prev.status,
        }));
      }

      dispatch(apiSuccess("GET", loadVisitKey, "Visit loaded"));
    } catch (error) {
      dispatch(
        apiFetchError(
          "GET",
          loadVisitKey,
          error?.response?.data?.error || "Unable to load visit.",
        ),
      );
      dispatch(
        showAlert({
          type: "error",
          title: t("Load failed"),
          message: error?.response?.data?.error || t("Unable to load visit."),
        }),
      );
    }
  };

  useEffect(() => {
    loadVisit();
  }, [id]);

  const handleCreatePayment = async () => {
    const amount = Number(form.amount);
    if (!Number.isFinite(amount) || amount <= 0) {
      dispatch(
        showAlert({
          type: "error",
          title: t("Invalid amount"),
          message: t("Please enter a valid amount greater than 0."),
        }),
      );
      return;
    }

    try {
      dispatch(apiRequest("POST", createPaymentKey));
      await api.post(`visits/${id}/payment`, {
        amount,
        method: form.method,
        status: form.status,
      });

      dispatch(apiSuccess("POST", createPaymentKey, "Payment saved"));
      dispatch(
        showAlert({
          type: "success",
          title: t("Payment saved"),
          message: t("Visit payment saved successfully."),
        }),
      );

      await loadVisit();
      navigate("/waiting-room");
    } catch (error) {
      dispatch(
        apiFetchError(
          "POST",
          createPaymentKey,
          error?.response?.data?.error || "Unable to save payment.",
        ),
      );
      dispatch(
        showAlert({
          type: "error",
          title: t("Submit failed"),
          message: error?.response?.data?.error || t("Unable to save payment."),
        }),
      );
    }
  };

  const paymentData = visitPayload?.payment || visit?.visitPayment || null;

  return (
    <div className={arClass(`min-h-screen pb-24 ${colors.background}`)}>
      {(isSubmitting || isLoadingVisit) && (
        <SubmitLoading
          message={
            isSubmitting
              ? textTranslator("Saving payment...", "PAYMENTS")
              : textTranslator("Loading visit payment data...", "PAYMENTS")
          }
        />
      )}

      <Breadcrumbs />

      <main className="w-full  mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8 space-y-6">
        <motion.header
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          className={arClass(`rounded-3xl border p-6 sm:p-8 ${colors.card}`)}
        >
          <div className={arClass("flex items-start justify-between gap-4") }>
            <div className={arClass("space-y-2") }>
              <div className={arClass("flex items-center gap-3") }>
                <span className="w-10 h-10 rounded-xl bg-blue-500/15 text-blue-600 flex items-center justify-center">
                  <Wallet size={20} />
                </span>
                <h1
                  className={arClass(
                    `text-2xl sm:text-3xl font-bold ${colors.text.primary}`,
                  )}
                >
                  <Text page="VISITS">Visit Payment</Text> #{id}
                </h1>
              </div>
              <p className={arClass(`text-sm sm:text-base ${colors.text.secondary}`)}>
                <Text page="VISITS">Create and save payment for this visit.</Text>
              </p>
            </div>
            <button
              type="button"
              onClick={loadVisit}
              disabled={isLoadingVisit}
              className={arClass(
                `rounded-xl px-4 py-2 text-sm font-semibold flex items-center justify-center gap-2 ${colors.button.primary}`,
              )}
            >
              {isLoadingVisit ? (
                <Loader2 className="animate-spin" size={16} />
              ) : null}
              <Text page="VISITS">Refresh</Text>
            </button>
          </div>
        </motion.header>

        {visit && (
          <section
            className={arClass(`rounded-3xl border p-5 sm:p-6 space-y-5 ${colors.card}`)}
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
              <div className={arClass(`rounded-2xl border p-4 ${colors.card}`)}>
                <p className={arClass(`text-xs ${colors.text.secondary}`)}>
                  <Text page="PATIENTS">Patient</Text>
                </p>
                <p className={arClass(`mt-2 text-sm font-semibold ${colors.text.primary}`)}>
                  {`${visit?.patient?.firstName || ""} ${visit?.patient?.lastName || ""}`.trim() ||
                    `#${visit?.patientId || "-"}`}
                </p>
              </div>

              <div className={arClass(`rounded-2xl border p-4 ${colors.card}`)}>
                <p className={arClass(`text-xs ${colors.text.secondary}`)}>
                  <Text page="VISITS">status</Text>
                </p>
                <p className={arClass(`mt-2 text-sm font-semibold ${colors.text.primary}`)}>
                  <Text page="VISITS">{displayValue(visit?.status)}</Text>
                </p>
              </div>

              <div className={arClass(`rounded-2xl border p-4 ${colors.card}`)}>
                <p className={arClass(`text-xs ${colors.text.secondary}`)}>
                  <Text page="VISITS">Current stage</Text>
                </p>
                <p className={arClass(`mt-2 text-sm font-semibold ${colors.text.primary}`)}>
                  <Text page="VISITS">{convertStageToStatus(visit?.currentStage)}</Text>
                </p>
              </div>

              <div className={arClass(`rounded-2xl border p-4 ${colors.card}`)}>
                <p className={arClass(`text-xs ${colors.text.secondary}`)}>
                  <Text page="VISITS">Updated at</Text>
                </p>
                <p className={arClass(`mt-2 text-sm font-semibold ${colors.text.primary}`)}>
                  {formatDate(visit?.updatedAt, true)}
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div className={arClass("rounded-2xl border p-4 space-y-2")}>
                <h3 className={arClass(`text-sm font-semibold ${colors.text.primary}`)}>
                  <Text page="VISITS">Visit Information</Text>
                </h3>
                <p className={arClass(`text-xs ${colors.text.secondary}`)}>
                  <Text page="VISITS">Visit ID</Text>: <span className={arClass(`font-medium text-xs ${colors.text.primary}`)}>{displayValue(visit?.id)}</span>
                </p>
                <p className={arClass(`text-xs ${colors.text.secondary}`)}>
                  <Text page="APPOINTMENTS">Appointment ID</Text>: <span className={arClass(`font-medium text-xs ${colors.text.primary}`)}>{displayValue(visit?.appointmentId)}</span>
                </p>
                <p className={arClass(`text-xs ${colors.text.secondary}`)}>
                  <Text page="VISITS">Created at</Text>: <span className={arClass(`font-medium text-xs ${colors.text.primary}`)}>{formatDate(visit?.createdAt, true)}</span>
                </p>
              </div>

              <div className={arClass("rounded-2xl border p-4 space-y-2")}>
                <h3 className={arClass(`text-sm font-semibold ${colors.text.primary}`)}>
                  <Text>Current Payment</Text>
                </h3>
                <p className={arClass(`text-xs ${colors.text.secondary}`)}>
                  <Text>Amount</Text>: <span className={arClass(`font-medium text-xs ${colors.text.primary}`)}>{displayValue(paymentData?.amount)}</span>
                </p>
                <p className={arClass(`text-xs ${colors.text.secondary}`)}>
                  <Text>Method</Text>: <span className={arClass(`font-medium text-xs ${colors.text.primary}`)}><Text>{displayValue(paymentData?.method)}</Text></span>
                </p>
                <p className={arClass(`text-xs ${colors.text.secondary}`)}>
                  <Text>Status</Text>: <span className={arClass(`font-medium text-xs ${colors.text.primary}`)}><Text>{displayValue(paymentData?.status)}</Text></span>
                </p>
              </div>
            </div>

            <div className={arClass(`rounded-2xl border p-4 sm:p-5 space-y-4 ${colors.card}`)}>
              <div className={arClass("flex items-center gap-2") }>
                <CreditCard size={18} className={arClass(colors.text.secondary)} />
                <h3 className={arClass(`text-sm font-semibold ${colors.text.primary}`)}>
                  <Text>Payment Details</Text>
                </h3>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <label className="space-y-1">
                  <span className={arClass(`text-xs font-semibold ${colors.text.secondary}`)}>
                    <Text>Amount</Text>
                  </span>
                  <input
                    value={form.amount}
                    type="number"
                    min={0}
                    step="0.01"
                    onChange={(event) => updateForm("amount", event.target.value)}
                    className={arClass(
                      `w-full rounded-xl border px-3 py-3 text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500/30 ${colors.input}`,
                    )}
                    placeholder={t("Enter amount")}
                  />
                </label>

                <label className="space-y-1">
                  <span className={arClass(`text-xs font-semibold ${colors.text.secondary}`)}>
                    <Text>Method</Text>
                  </span>
                  <select
                    value={form.method}
                    onChange={(event) => updateForm("method", event.target.value)}
                    className={arClass(
                      `w-full rounded-xl border px-3 py-2 text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500/30 ${colors.input}`,
                    )}
                  >
                    {paymentMethods.map((method) => (
                      <option key={method} value={method}>
                        <Text>{method}</Text>
                      </option>
                    ))}
                  </select>
                </label>

                <label className="space-y-1">
                  <span className={arClass(`text-xs font-semibold ${colors.text.secondary}`)}>
                    <Text>Status</Text>
                  </span>
                  <select
                    value={form.status}
                    onChange={(event) => updateForm("status", event.target.value)}
                    className={arClass(
                      `w-full rounded-xl border px-3 py-2 text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500/30 ${colors.input}`,
                    )}
                  >
                    {paymentStatuses.map((status) => (
                      <option key={status} value={status}>
                        <Text>{status}</Text>
                      </option>
                    ))}
                  </select>
                </label>
              </div>

              <div className="flex justify-end border-t pt-4">
                <button
                  type="button"
                  onClick={handleCreatePayment}
                  disabled={!visit?.id || isSubmitting}
                  className={arClass(
                    `px-4 py-2.5 rounded-xl text-sm font-semibold flex items-center gap-2 ${colors.button.primary}`,
                  )}
                >
                  <Text>Save payment</Text>
                  {defaultLanguage === "ar" ? <ArrowLeft size={16} /> : <ArrowRight size={16} />}
                </button>
              </div>
            </div>
          </section>
        )}
      </main>
    </div>
  );
};

export default VisitPayment;
